import { effectiveConsent } from "@/lib/consent";
import { getAttribution } from "@/lib/attribution";
import { isFramed } from "@/lib/framed";

export interface LeadValues {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  city?: string | null;
}

export async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const normalizeEmail = (e: string) => e.trim().toLowerCase();
export const normalizeText = (t: string) => t.trim().toLowerCase().replace(/\s+/g, " ");
export function normalizePhone(p: string): string | null {
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (d.length >= 8 && d.length <= 15 && p.trim().startsWith("+")) return `+${d}`;
  return null;
}

/** Visitors who opted out or send a privacy signal get no personal details shared. */
export function mayShareUserData(): boolean {
  return !effectiveConsent().ccpa_opt_out;
}

function buildUserData(v: LeadValues) {
  const t = (x?: string | null) => (x ? x.trim().replace(/\s+/g, " ") : "") || undefined;
  const [first, ...rest] = (v.name ?? "").trim().split(/\s+/);
  return {
    email: v.email ? normalizeEmail(v.email) || undefined : undefined,
    phone_number: (v.phone ? normalizePhone(v.phone) : null) ?? undefined,
    company: t(v.company),
    address: { first_name: t(first), last_name: t(rest.join(" ")), city: t(v.city) },
  };
}

const sent = new Set<string>();
const newId = () => { try { return crypto.randomUUID(); } catch { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; } };

let lastLead: { form: string; values: LeadValues; leadSource?: string } | null = null;

function campaign(leadSource?: string) {
  let a: Record<string, unknown> = {};
  try { a = (getAttribution() as unknown as Record<string, unknown>) ?? {}; } catch { /* none */ }
  const s = (k: string) => (typeof a[k] === "string" ? (a[k] as string) : undefined);
  return {
    lead_source: leadSource,
    page_path: window.location.pathname,
    utm_source: s("utm_source"),
    utm_medium: s("utm_medium"),
    utm_campaign: s("utm_campaign"),
    gclid: s("gclid"),
    campaign_id: s("campaignid") ?? s("gad_campaignid"),
  };
}

async function push(event: string, form: string, values: LeadValues, leadSource?: string) {
  if (isFramed() || window.location.pathname.startsWith("/112076")) return;
  const key = `${event}|${form}|${values.email ?? ""}|${values.phone ?? ""}`;
  if (sent.has(key)) return;
  sent.add(key);
  const payload: Record<string, unknown> = { event, event_id: newId(), form_name: form, ...campaign(leadSource) };
  if (mayShareUserData()) payload.user_data = buildUserData(values);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ user_data: undefined });
  window.dataLayer.push(payload);
}

/** Sends `generate_lead` to Tag Manager after a form saved. Never throws. */
export async function pushLeadEvent(form: string, values: LeadValues, leadSource?: string) {
  lastLead = { form, values, leadSource };
  try { await push("generate_lead", form, values, leadSource); } catch { /* never interrupt */ }
}

/** Sends `booking_completed`, reusing the details from the form that led to the booking. */
export async function pushBookingEvent(form = "booking") {
  const l = lastLead ?? { form, values: {} };
  try { await push("booking_completed", l.form, l.values, l.leadSource); } catch { /* never interrupt */ }
}

export function resetGtmLeadForTests() { lastLead = null; sent.clear(); }
